import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

export default function BookingGrid({ username }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [activeTab, setActiveTab] = useState('new'); // 'new', 'confirmed', or 'cancelled'

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      setUpdatingId(bookingId);
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingDoc = await getDoc(bookingRef);
      const bookingData = bookingDoc.data();

      if (!bookingData) {
        throw new Error('Booking not found');
      }

      // Update booking status
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Update availability slots if needed
      if (bookingData.date?.date && bookingData.hour?.hour) {
        const modelRef = doc(db, 'models', username);
        const modelDoc = await getDoc(modelRef);
        const modelData = modelDoc.data();
        
        if (modelData?.availabilitySlots) {
          const date = bookingData.date.date.toDate ? 
            bookingData.date.date.toDate() : 
            new Date(bookingData.date.date);
          const dayOfWeek = date.getDay();
          const hour = bookingData.hour.hour;
          
          const updatedSlots = modelData.availabilitySlots.map(slot => {
            if (slot.dayOfWeek === dayOfWeek && slot.hour === hour) {
              // If booking is confirmed, mark slot as unavailable
              // If booking is cancelled, mark slot as available
              return {
                ...slot,
                isAvailable: newStatus === 'cancelled'
              };
            }
            return slot;
          });

          await updateDoc(modelRef, {
            availabilitySlots: updatedSlots
          });
        }
      }

      // Update local state
      setBookings(bookings.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: newStatus }
          : booking
      ));
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Erro ao atualizar status do agendamento');
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const bookingsRef = collection(db, 'bookings');
        const q = query(bookingsRef, where('username', '==', username));
        const querySnapshot = await getDocs(q);
        
        const bookingsList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Raw booking data:', {
            id: doc.id,
            date: data.date,
            dateType: typeof data.date,
            dateValue: data.date?.date,
            dateValueType: typeof data.date?.date,
            dateValueString: data.date?.date?.toString(),
            ...data
          });
          return {
            id: doc.id,
            ...data
          };
        });
        
        setBookings(bookingsList);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchBookings();
    }
  }, [username]);

  const filteredBookings = bookings.filter(booking => {
    switch (activeTab) {
      case 'new':
        return booking.status === 'new';
      case 'confirmed':
        return booking.status === 'confirmed';
      case 'cancelled':
        return booking.status === 'cancelled';
      default:
        return false;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-gray-50 rounded-xl p-8">
        <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-600 text-xl font-medium">Nenhum agendamento encontrado</p>
        <p className="text-gray-500 text-base mt-2 text-center max-w-md">
          Os agendamentos aparecerão aqui quando forem criados
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('new')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
            activeTab === 'new'
              ? 'text-pink-600 border-b-2 border-pink-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Novos Agendamentos
        </button>
        <button
          onClick={() => setActiveTab('confirmed')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
            activeTab === 'confirmed'
              ? 'text-pink-600 border-b-2 border-pink-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Confirmados
        </button>
        <button
          onClick={() => setActiveTab('cancelled')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
            activeTab === 'cancelled'
              ? 'text-pink-600 border-b-2 border-pink-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Cancelados
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
        {filteredBookings.map((booking) => (
          <div
            key={booking.id}
            className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-gray-900">{booking.name}</h3>
                <p className="text-gray-600">{booking.whatsapp}</p>
              </div>
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                booking.status === 'new' ? 'bg-emerald-50 text-emerald-700' :
                booking.status === 'confirmed' ? 'bg-indigo-50 text-indigo-700' :
                booking.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                'bg-gray-50 text-gray-700'
              }`}>
                {booking.status === 'new' ? 'Novo' :
                 booking.status === 'confirmed' ? 'Confirmado' :
                 booking.status === 'cancelled' ? 'Cancelado' :
                 'Outro'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-500 mb-1">Data</p>
                <p className="text-lg font-semibold text-gray-900">
                  {booking.date?.date ? (() => {
                    if (booking.date.date.seconds) {
                      const timestamp = booking.date.date.seconds * 1000;
                      const date = new Date(timestamp);
                      
                      const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                      
                      const weekday = weekdays[date.getDay()];
                      const month = months[date.getMonth()];
                      const day = date.getDate();
                      
                      return `${weekday}, ${day} ${month}`;
                    }
                    return 'Data não especificada';
                  })() : 'Data não especificada'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-500 mb-1">Horário</p>
                <p className="text-lg font-semibold text-gray-900">
                  {booking.hour?.hour}:00
                </p>
              </div>
            </div>

            {booking.note && (
              <div className="mb-6 bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Observação</p>
                <p className="text-gray-700">{booking.note}</p>
              </div>
            )}

            {booking.status === 'new' && (
              <div className="flex gap-3">
                <button
                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                  disabled={updatingId === booking.id}
                  className="flex-1 bg-pink-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                >
                  {updatingId === booking.id ? (
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {updatingId === booking.id ? 'Confirmando...' : 'Confirmar'}
                </button>
                <button
                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                  disabled={updatingId === booking.id}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                >
                  {updatingId === booking.id ? (
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  {updatingId === booking.id ? 'Cancelando...' : 'Cancelar'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
} 